import { get } from 'lodash';
import { getDescription } from '../lib/getDescription';
import { stripQueryStringFromCdnUrl } from '../services/getUrlFromCdn';
import { isMultiPrice, isSinglePwyw, selectablePrices } from '../lib/multiPrice';
import { PAGE_TYPES } from './page-types';
import { checkoutTypes } from './checkout-types';
import { checkoutLayouts } from './checkout-layouts';

function logoRendersInsideCard({ theme, checkoutType }) {
  const hostedPageLogoStyle = theme.elements?.product?.logo?.hostedPageLogoStyle;
  return (
    checkoutType === checkoutTypes.EMBED ||
    checkoutType === checkoutTypes.POPUP ||
    (checkoutType === checkoutTypes.CHECKOUT && hostedPageLogoStyle === 'logoBubble')
  );
}

// Position 'form' moves the picker out of the product content into the layout's
// form area (Double's form column, Center/Split's form section). Legacy
// double_banner has no form slot for it, and an unknown layout is treated as
// content, so a caller that can't supply one still gets the picker counted.
export function pickerRendersInForm({ theme, checkoutLayout }) {
  return (
    (theme.elements?.product?.pricePicker?.position || 'content') === 'form' &&
    [checkoutLayouts.DOUBLE, checkoutLayouts.CENTER, checkoutLayouts.SPLIT].includes(
      checkoutLayout,
    )
  );
}

const Modules = {
  MEDIA: 'media',
  LOGO: 'logo',
  TITLE: 'productTitle',
  PRICE: 'price',
  PRICE_PICKER: 'pricePicker',
  SETUP_FEE: 'setupFee',
  DESCRIPTION: 'description',
  DATE: 'date',
  LOCATION: 'location',
  MEETING_LINK: 'meetingLink',
};

export function getFormModules({
  theme,
  checkout,
  isScreenshot,
  query = {},
  isPreview,
  checkoutType,
}) {
  const modules = {};
  const elements = theme.elements || {};

  if (checkout.images?.length > 0) {
    modules[Modules.MEDIA] = true;
  }

  if (
    checkIsEnabled(elements, 'product.logo') &&
    !isScreenshot &&
    logoRendersInsideCard({ theme, checkoutType })
  ) {
    modules[Modules.LOGO] = true;
  }

  if (
    checkIsEnabled(elements, 'product.title') &&
    (checkout.title || (checkout.allowDynamicTitle && query.title))
  ) {
    modules[Modules.TITLE] = true;
  }

  const description = (() => {
    const description = getDescription({
      allowDynamicDescription: checkout.allowDynamicDescription,
      dynamicDescription: query.description,
      checkoutDescription: checkout.description,
      checkoutDescriptionHtml: checkout.descriptionHtml,
    });

    if (isPreview) {
      return stripQueryStringFromCdnUrl(description);
    }

    return description;
  })();

  if (description && checkIsEnabled(elements, 'description')) {
    modules[Modules.DESCRIPTION] = true;
  }

  return modules;
}

export function getEventModules({
  theme,
  checkout,
  isScreenshot,
  query = {},
  isPreview,
  checkoutType,
}) {
  const modules = {};
  const elements = theme.elements || {};

  if (checkout.images?.length > 0) {
    modules[Modules.MEDIA] = true;
  }

  if (
    checkIsEnabled(elements, 'product.logo') &&
    !isScreenshot &&
    logoRendersInsideCard({ theme, checkoutType })
  ) {
    modules[Modules.LOGO] = true;
  }

  if (
    checkIsEnabled(elements, 'product.title') &&
    (checkout.title || (checkout.allowDynamicTitle && query.title))
  ) {
    modules[Modules.TITLE] = true;
  }

  const description = (() => {
    const description = getDescription({
      allowDynamicDescription: checkout.allowDynamicDescription,
      dynamicDescription: query.description,
      productDescription: checkout.eventDetails.description,
      productDescriptionHtml: checkout.eventDetails.descriptionHtml,
      checkoutDescription: checkout.description,
      checkoutDescriptionHtml: checkout.descriptionHtml,
    });

    if (isPreview) {
      return stripQueryStringFromCdnUrl(description);
    }

    return description;
  })();

  if (description && checkIsEnabled(elements, 'description')) {
    modules[Modules.DESCRIPTION] = true;
  }

  if (
    checkIsEnabled(elements, 'product.event.location') &&
    checkout?.eventDetails?.location &&
    (checkout?.eventDetails?.type === 'in_person' || checkout?.eventDetails?.type === 'hybrid')
  ) {
    modules[Modules.LOCATION] = true;
  }

  if (checkIsEnabled(elements, 'product.event.date') && checkout?.eventDetails?.startDate) {
    modules[Modules.DATE] = true;
  }

  if (
    checkIsEnabled(elements, 'product.event.virtual') &&
    (checkout?.eventDetails?.type === 'virtual' || checkout?.eventDetails?.type === 'hybrid')
  ) {
    modules[Modules.MEETING_LINK] = true;
  }

  return modules;
}

function checkIsEnabled(elements, elementPath) {
  const enabled =
    typeof get(elements, `${elementPath}.enabled`) === 'boolean'
      ? get(elements, `${elementPath}.enabled`)
      : true;
  return enabled;
}

export function getProductModules({
  theme,
  checkout,
  isScreenshot,
  query = {},
  productPriceAmount,
  setupFeeAmount,
  discountedFromPrice,
  trialPeriodDays,
  startDate,
  isPreview,
  checkoutType,
  checkoutLayout,
}) {
  const modules = {};
  const elements = theme.elements || {};

  if (checkout.product.media?.length > 0) {
    modules[Modules.MEDIA] = true;
  }

  if (
    checkIsEnabled(elements, 'product.logo') &&
    !isScreenshot &&
    logoRendersInsideCard({ theme, checkoutType })
  ) {
    modules[Modules.LOGO] = true;
  }

  if (
    checkIsEnabled(elements, 'product.title') &&
    (checkout.product.title || (checkout.allowDynamicTitle && query.title))
  ) {
    modules[Modules.TITLE] = true;
  }

  const pricingEnabled = checkIsEnabled(elements, 'product.pricing');
  const willPriceRender =
    checkIsEnabled(elements, 'product.pricing.price') &&
    (productPriceAmount || discountedFromPrice);
  const willTrialPeriodRender =
    checkIsEnabled(elements, 'product.pricing.trialPeriod') && trialPeriodDays;
  const willStartDateRender =
    checkIsEnabled(elements, 'product.pricing.startDate') && startDate;
  // A single PWYW price renders its amount INPUT in the price block, and the
  // Highlight badge renders there too — both must keep the block alive even
  // when the amount is 0/unset. Only the bare price display hides on a 0
  // amount (Price self-gates via shouldShowPrice).
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type/payWhatYouWant via isSinglePwyw)
  const willPwywRender = isSinglePwyw(checkout.product, query);
  const willBadgeRender =
    checkIsEnabled(elements, 'product.pricing.badge') &&
    !!selectablePrices(checkout.product?.prices, query)[0]?.badge;

  // The single-PWYW amount input is a required buyer input, not a price
  // display, so it keeps the block alive regardless of the `product.pricing`
  // element toggle. Legacy pricingType:'multiple' products commonly ship with
  // `product.pricing` disabled (they showed pricing via variants); reinterpreted
  // as single PWYW they'd otherwise render no amount input at all. The dedicated
  // `checkout.form.pwyw` toggle still hides the input for merchants who want to.
  if (
    (pricingEnabled &&
      (willPriceRender ||
        willBadgeRender ||
        willTrialPeriodRender ||
        willStartDateRender)) ||
    willPwywRender
  ) {
    modules[Modules.PRICE] = true;
  }

  // The picker carries its own `enabled` toggle, independent of the single-price
  // block's `product.pricing` — they're separate elements. Mirrors the self-gate
  // in ProductPricePicker so the two can't disagree about whether it renders.
  if (
    checkIsEnabled(elements, 'product.pricePicker') &&
    isMultiPrice(checkout.product?.prices, query) &&
    !pickerRendersInForm({ theme, checkoutLayout })
  ) {
    modules[Modules.PRICE_PICKER] = true;
  }

  if (
    checkIsEnabled(elements, 'product.pricing') &&
    checkIsEnabled(elements, 'product.pricing.setupFee') &&
    setupFeeAmount
  ) {
    modules[Modules.SETUP_FEE] = true;
  }

  const description = (() => {
    const description = getDescription({
      allowDynamicDescription: checkout.allowDynamicDescription,
      dynamicDescription: query.description,
      productDescription: checkout.product.description,
      productDescriptionHtml: checkout.product.descriptionHtml,
      checkoutDescription: checkout.description,
      checkoutDescriptionHtml: checkout.descriptionHtml,
    });

    if (isPreview) {
      return stripQueryStringFromCdnUrl(description);
    }

    return description;
  })();

  if (description && checkIsEnabled(elements, 'description')) {
    modules[Modules.DESCRIPTION] = true;
  }

  return modules;
}

export function checkHasProductModules({
  theme,
  checkout,
  seller,
  isScreenshot,
  query = {},
  productPriceAmount,
  setupFeeAmount,
  discountedFromPrice,
  trialPeriodDays,
  startDate,
  checkoutType,
  checkoutLayout,
}) {
  let modules = {};

  if (checkout.type === PAGE_TYPES.CHECKOUT) {
    modules = getProductModules({
      theme,
      checkout,
      seller,
      isScreenshot,
      query,
      productPriceAmount,
      setupFeeAmount,
      discountedFromPrice,
      trialPeriodDays,
      startDate,
      checkoutType,
      checkoutLayout,
    });
  } else if (checkout.type === PAGE_TYPES.FORM) {
    modules = getFormModules({ theme, checkout, seller, checkoutType });
  } else if (checkout.type === PAGE_TYPES.EVENT) {
    return false;
  }

  return Object.keys(modules).length > 0;
}

export function checkHasEventModules({ theme, checkout, seller }) {
  if (checkout.type !== PAGE_TYPES.EVENT) {
    return false;
  }

  return Object.keys(getEventModules({ theme, checkout, seller })).length > 0;
}

function hasLogoBubble(modules, theme, checkoutType) {
  if (!modules[Modules.LOGO]) return false;
  return (
    checkoutType === checkoutTypes.EMBED ||
    checkoutType === checkoutTypes.POPUP ||
    (checkoutType === checkoutTypes.CHECKOUT &&
      theme.elements?.product?.logo?.hostedPageLogoStyle === 'logoBubble')
  );
}

// True when the Product/Form section renders inline content besides the bare
// media image (title, price, description, etc., or a logo bubble). When false,
// Product.js cancels its pb-4 with marginBottom:-1rem so the media flushes
// against the divider — see Product.js for the flushing logic this mirrors.
export function checkHasProductWrapperContent(args) {
  let modules = {};
  if (args.checkout.type === PAGE_TYPES.CHECKOUT) {
    modules = getProductModules(args);
  } else if (args.checkout.type === PAGE_TYPES.FORM) {
    modules = getFormModules(args);
  } else {
    return false;
  }

  return Boolean(
    hasLogoBubble(modules, args.theme, args.checkoutType) ||
      modules[Modules.TITLE] ||
      modules[Modules.PRICE] ||
      modules[Modules.PRICE_PICKER] ||
      modules[Modules.SETUP_FEE] ||
      modules[Modules.DESCRIPTION],
  );
}

// Same as above for EventDetails — see EventDetails.js for the parallel flushing logic.
export function checkHasEventWrapperContent({ theme, checkout, seller, checkoutType }) {
  if (checkout.type !== PAGE_TYPES.EVENT) {
    return false;
  }
  const modules = getEventModules({ theme, checkout, seller });
  return Boolean(
    hasLogoBubble(modules, theme, checkoutType) ||
      modules[Modules.TITLE] ||
      modules[Modules.PRICE] ||
      modules[Modules.DATE] ||
      modules[Modules.LOCATION] ||
      modules[Modules.MEETING_LINK] ||
      modules[Modules.DESCRIPTION],
  );
}

export default Modules;
